import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@api/client';
import type { CurrentSubscriptionResponse } from '@schemas/api';
import { useAuthStore } from '@store/authStore';

export const useCurrentSubscription = () => {
  const currentUser = useAuthStore((state) => state.currentUser);
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery({
    queryKey: ['subscription', tenantScope, 'current'],
    queryFn: async () => {
      const { data } = await apiClient.get<CurrentSubscriptionResponse>(
        '/subscriptions/current',
      );
      return data;
    },
    enabled: Boolean(currentUser),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
};

export const usePlanFeature = (feature: string): boolean => {
  const { data } = useCurrentSubscription();
  // Read from entitlements (resolved with overrides) when present, fall back to plan.features.
  const f = data?.entitlements?.features ?? data?.plan?.features ?? {};
  // Enterprise sentinel: _all_features=true grants everything
  if (f['_all_features'] === true) return true;
  return f[feature] === true;
};

export const usePlanFeatures = (): Record<string, boolean> => {
  const { data } = useCurrentSubscription();
  const f = data?.entitlements?.features ?? data?.plan?.features ?? {};
  // Surface the resolved record; consumers do their own checks
  return (f as Record<string, boolean>) ?? {};
};
